import { NumberInput, TextInput } from '@jana-technology/platform-ui'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Tooltip } from '@mui/material'

import { IAcceptanceCategory } from 'app/services/api/acceptanceCategories'

import { RiskUnitType } from '..'

interface IThresholdDisplayProps {
  riskType: RiskUnitType
  riskThresholds: IAcceptanceCategory[] | undefined
}

interface IDisplayThresholdRowProps {
  isRiskDensity: boolean
  thresholdRow: IAcceptanceCategory
}

const RiskThresholdLevelColorBlock = ({
  thresholdName,
  thresholdColor
}: {
  thresholdName: string
  thresholdColor: string
}) => {
  return (
    <div className='flex items-center'>
      <div
        className='flex-none mr-2 size-10 rounded-sm'
        style={{ backgroundColor: '#' + thresholdColor }}
      ></div>
      <div>{thresholdName}</div>
    </div>
  )
}

const DisplayThresholdRow = ({
  isRiskDensity,
  thresholdRow
}: IDisplayThresholdRowProps) => {
  return (
    <div className='grid grid-cols-[8rem_1fr_1fr] gap-x-4 items-center my-4'>
      <RiskThresholdLevelColorBlock
        thresholdName={thresholdRow.name}
        thresholdColor={thresholdRow.colour}
      />
      <div className='flex items-center'>
        <NumberInput
          min={1}
          max={Number.MAX_VALUE}
          value={
            isRiskDensity ? thresholdRow.minDensityValue : thresholdRow.minValue
          }
          disabled={true}
        />
      </div>
      <div className='flex items-center'>
        {(() => {
          let isMax = false
          let value = 0

          if (isRiskDensity) {
            isMax = thresholdRow.maxDensityValue >= Number.MAX_VALUE
            value = thresholdRow.maxDensityValue
          } else {
            isMax = thresholdRow.maxValue >= Number.MAX_VALUE
            value = thresholdRow.maxValue
          }

          if (isMax) {
            return <TextInput value='MAX' disabled />
          } else {
            return (
              <NumberInput
                min={1}
                max={Number.MAX_VALUE}
                value={value}
                disabled={true}
              />
            )
          }
        })()}
      </div>
    </div>
  )
}

export const ThresholdDisplay = ({
  riskType,
  riskThresholds
}: IThresholdDisplayProps) => {
  const isRiskDensity = riskType === RiskUnitType.RiskDensity
  const riskUnitDisplay = isRiskDensity ? 'RU/mi/yr' : 'RU/yr'

  return (
    <div className='tracking-tight'>
      <div className='flex justify-between'>
        <div className='mb-5 font-bold text-blue-navy uppercase flex'>
          <div className='mr-1 pt-[1px]'>Risk Threshold</div>
          <Tooltip
            title='Risk units can only be edited via the Configuration Dashboard Page.'
            slotProps={{
              tooltip: {
                sx: {
                  borderRadius: 2,
                  width: 230,
                  opacity: 0.85,
                  color: 'rgba(255,255,255,0.87)',
                  textAlign: 'center'
                }
              }
            }}
          >
            <InfoOutlinedIcon color='action' />
          </Tooltip>
        </div>
        <div className='font-semibold'>{riskUnitDisplay}</div>
      </div>

      <div className='grid grid-cols-[8rem_1fr_1fr] gap-x-4 items-center font-semibold mb-6'>
        <div className='whitespace-nowrap'>Risk Level</div>
        <div>From</div>
        <div>To</div>
      </div>

      {riskThresholds?.map((thresholdRow) => (
        <DisplayThresholdRow
          key={thresholdRow.code}
          isRiskDensity={isRiskDensity}
          thresholdRow={thresholdRow}
        />
      ))}
    </div>
  )
}
